import React, { useState, useEffect, useCallback } from 'react';
import {
  AreaChart, Area, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { AnyService } from '../../../types/service.type';
import { servicesService, ServiceMetrics, MetricSeries } from '../../../services/services.service';

interface MonitoringTabProps {
  service: AnyService;
}

// ─── constants ───────────────────────────────────────────────────────────────

const RANGES = [
  { label: '1h',  hours: 1,   period: 300 },
  { label: '3h',  hours: 3,   period: 300 },
  { label: '12h', hours: 12,  period: 600 },
  { label: '24h', hours: 24,  period: 900 },
  { label: '7d',  hours: 168, period: 3600 },
];

// ─── helpers ─────────────────────────────────────────────────────────────────

function fmtTs(iso: string, rangeHours: number): string {
  const d = new Date(iso);
  if (rangeHours <= 24) {
    return d.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', hour12: false });
  }
  return d.toLocaleDateString('es-MX', {
    month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: false,
  });
}

function toChartData(series: MetricSeries | undefined, rangeHours: number) {
  if (!series?.timestamps.length) return [];
  return series.timestamps.map((ts, i) => ({ time: fmtTs(ts, rangeHours), value: series.values[i] ?? 0 }));
}

function toNetworkData(
  inSeries: MetricSeries | undefined,
  outSeries: MetricSeries | undefined,
  rangeHours: number,
) {
  if (!inSeries?.timestamps.length) return [];
  return inSeries.timestamps.map((ts, i) => ({
    time: fmtTs(ts, rangeHours),
    entrada: inSeries.values[i] ?? 0,
    salida: outSeries?.values[i] ?? 0,
  }));
}

function stat(val: number | null | undefined, unit: string) {
  if (val === null || val === undefined) return '—';
  return unit === '%' ? `${val}%` : `${val} ${unit}`;
}

// ─── sub-components ──────────────────────────────────────────────────────────

const NoData: React.FC = () => (
  <div className="h-44 flex items-center justify-center text-sm text-gray-400 dark:text-gray-600">
    Sin datos en este rango
  </div>
);

const ChartCard: React.FC<{
  title: string;
  subtitle?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, subtitle, children }) => (
  <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
    <div className="flex items-center justify-between mb-3">
      <h4 className="text-sm font-semibold text-gray-900 dark:text-white">{title}</h4>
      {subtitle && <div className="text-xs text-gray-500 dark:text-gray-400">{subtitle}</div>}
    </div>
    {children}
  </div>
);

// ─── MonitoringTab ────────────────────────────────────────────────────────────

const MonitoringTab: React.FC<MonitoringTabProps> = ({ service }) => {
  const serviceId = (service as any).id as string;
  const category = ((service as any).category || (service as any).type || '').toUpperCase();
  const isRds = ['DATABASE', 'DB'].includes(category);

  const [rangeIdx, setRangeIdx] = useState(0);
  const [data, setData] = useState<ServiceMetrics | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const range = RANGES[rangeIdx];

  const load = useCallback(async () => {
    if (!serviceId) return;
    setIsLoading(true);
    setError(null);
    try {
      const result = await servicesService.getMetrics(serviceId, range.hours, range.period);
      setData(result);
    } catch (e: any) {
      setError(e?.message || 'Error al cargar métricas');
    } finally {
      setIsLoading(false);
    }
  }, [serviceId, range.hours, range.period]);

  useEffect(() => { load(); }, [load]);

  // ── loading ──
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-white mb-3" />
          <p className="text-sm text-gray-500 dark:text-gray-400">Cargando métricas…</p>
        </div>
      </div>
    );
  }

  // ── error ──
  if (error) {
    return (
      <div className="space-y-4">
        <RangeSelector rangeIdx={rangeIdx} setRangeIdx={setRangeIdx} onRefresh={load} />
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      </div>
    );
  }

  // ── no metrics ──
  if (data && !data.hasMetrics) {
    const isStatic = ['APP_DEPLOY_STATIC', 'SITE'].includes(data.serviceType?.toUpperCase() ?? '');
    return (
      <div className="space-y-4">
        <RangeSelector rangeIdx={rangeIdx} setRangeIdx={setRangeIdx} onRefresh={load} />
        <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-10 text-center">
          {isStatic ? (
            <svg className="mx-auto h-12 w-12 text-gray-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
            </svg>
          ) : (
            <svg className="mx-auto h-12 w-12 text-gray-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          )}
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {isStatic ? 'Métricas de CloudFront' : 'Sin métricas disponibles'}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {data.message || 'No hay métricas de infraestructura disponibles para este servicio.'}
          </p>
        </div>
      </div>
    );
  }

  const m = data?.metrics ?? {};

  // ── empty notice ──
  const isEmpty = data && Object.values(m).every((s) => s.timestamps.length === 0);

  return (
    <div className="space-y-5">
      {/* Header row */}
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold text-gray-900 dark:text-white">Métricas de infraestructura</h3>
        <RangeSelector rangeIdx={rangeIdx} setRangeIdx={setRangeIdx} onRefresh={load} />
      </div>

      {/* Empty data notice */}
      {isEmpty && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3 text-sm text-yellow-700 dark:text-yellow-300">
          Sin datos en este rango. El servicio puede estar detenido o usar monitoreo básico (intervalos de 5 min).
        </div>
      )}

      {/* Summary cards */}
      {data && !isEmpty && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {isRds ? (
            <>
              <SummaryCard label="CPU actual" value={stat(m.cpu?.current, '%')} color="blue" />
              <SummaryCard label="Conexiones" value={stat(m.connections?.current, 'conex')} color="purple" />
              <SummaryCard label="Storage libre" value={stat(m.free_storage?.current, 'GB')} color="green" />
              <SummaryCard label="Memoria libre" value={stat(m.freeable_memory?.current, 'MB')} color="teal" />
            </>
          ) : (
            <>
              <SummaryCard label="CPU actual" value={stat(m.cpu?.current, '%')} color="blue" />
              <SummaryCard label="CPU máx" value={stat(m.cpu?.max, '%')} color="orange" />
              <SummaryCard label="Red entrada" value={stat(m.network_in?.current, 'KB')} color="green" />
              <SummaryCard label="Red salida" value={stat(m.network_out?.current, 'KB')} color="purple" />
            </>
          )}
        </div>
      )}

      {/* ── EC2 charts ── */}
      {!isRds && (
        <>
          {/* CPU */}
          <ChartCard
            title="CPU"
            subtitle={<>Actual: <strong>{stat(m.cpu?.current, '%')}</strong> · Máx: <strong>{stat(m.cpu?.max, '%')}</strong></>}
          >
            {toChartData(m.cpu, range.hours).length > 0 ? (
              <ResponsiveContainer width="100%" height={180}>
                <AreaChart data={toChartData(m.cpu, range.hours)} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
                  <defs>
                    <linearGradient id="cpuFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor="#111827" stopOpacity={0.2} />
                      <stop offset="95%" stopColor="#111827" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="time" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 10 }} domain={[0, 100]} unit="%" />
                  <Tooltip formatter={(v: any) => [`${v}%`, 'CPU']} />
                  <Area type="monotone" dataKey="value" stroke="#111827" fill="url(#cpuFill)" strokeWidth={2} dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            ) : <NoData />}
          </ChartCard>

          {/* Network */}
          <ChartCard
            title="Red"
            subtitle={
              <div className="flex gap-3">
                <span className="flex items-center gap-1"><span className="inline-block w-3 h-0.5 bg-blue-500" /> Entrada</span>
                <span className="flex items-center gap-1"><span className="inline-block w-3 h-0.5 bg-purple-500" /> Salida</span>
              </div>
            }
          >
            {toNetworkData(m.network_in, m.network_out, range.hours).length > 0 ? (
              <ResponsiveContainer width="100%" height={180}>
                <LineChart data={toNetworkData(m.network_in, m.network_out, range.hours)} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="time" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 10 }} unit=" KB" />
                  <Tooltip formatter={(v: any, name: any) => [`${v} KB`, name === 'entrada' ? 'Entrada' : 'Salida']} />
                  <Line type="monotone" dataKey="entrada" stroke="#3b82f6" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="salida"  stroke="#a855f7" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            ) : <NoData />}
          </ChartCard>

          {/* Disk */}
          {m.disk_read && (
            <ChartCard
              title="Disco"
              subtitle={
                <div className="flex gap-3">
                  <span className="flex items-center gap-1"><span className="inline-block w-3 h-0.5 bg-green-500" /> Lectura</span>
                  <span className="flex items-center gap-1"><span className="inline-block w-3 h-0.5 bg-orange-500" /> Escritura</span>
                </div>
              }
            >
              {toNetworkData(m.disk_read, m.disk_write, range.hours).length > 0 ? (
                <ResponsiveContainer width="100%" height={180}>
                  <LineChart data={toNetworkData(m.disk_read, m.disk_write, range.hours)} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="time" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                    <YAxis tick={{ fontSize: 10 }} unit=" KB" />
                    <Tooltip formatter={(v: any, name: any) => [`${v} KB`, name === 'entrada' ? 'Lectura' : 'Escritura']} />
                    <Line type="monotone" dataKey="entrada" stroke="#22c55e" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="salida"  stroke="#f97316" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              ) : <NoData />}
            </ChartCard>
          )}
        </>
      )}

      {/* ── RDS charts ── */}
      {isRds && (
        <>
          {/* RDS CPU */}
          <ChartCard
            title="CPU"
            subtitle={<>Actual: <strong>{stat(m.cpu?.current, '%')}</strong></>}
          >
            {toChartData(m.cpu, range.hours).length > 0 ? (
              <ResponsiveContainer width="100%" height={180}>
                <AreaChart data={toChartData(m.cpu, range.hours)} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
                  <defs>
                    <linearGradient id="rdsCpuFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor="#8b5cf6" stopOpacity={0.25} />
                      <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="time" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 10 }} domain={[0, 100]} unit="%" />
                  <Tooltip formatter={(v: any) => [`${v}%`, 'CPU']} />
                  <Area type="monotone" dataKey="value" stroke="#8b5cf6" fill="url(#rdsCpuFill)" strokeWidth={2} dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            ) : <NoData />}
          </ChartCard>

          {/* RDS Connections + Storage */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <ChartCard title="Conexiones" subtitle={<>Actual: <strong>{stat(m.connections?.current, 'conex')}</strong></>}>
              {toChartData(m.connections, range.hours).length > 0 ? (
                <ResponsiveContainer width="100%" height={150}>
                  <AreaChart data={toChartData(m.connections, range.hours)} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="time" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip formatter={(v: any) => [`${v}`, 'Conexiones']} />
                    <Area type="monotone" dataKey="value" stroke="#06b6d4" fill="#06b6d4" fillOpacity={0.15} strokeWidth={2} dot={false} />
                  </AreaChart>
                </ResponsiveContainer>
              ) : <NoData />}
            </ChartCard>

            <ChartCard title="Storage libre" subtitle={<>Actual: <strong>{stat(m.free_storage?.current, 'GB')}</strong></>}>
              {toChartData(m.free_storage, range.hours).length > 0 ? (
                <ResponsiveContainer width="100%" height={150}>
                  <AreaChart data={toChartData(m.free_storage, range.hours)} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="time" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                    <YAxis tick={{ fontSize: 10 }} unit=" GB" />
                    <Tooltip formatter={(v: any) => [`${v} GB`, 'Storage libre']} />
                    <Area type="monotone" dataKey="value" stroke="#22c55e" fill="#22c55e" fillOpacity={0.15} strokeWidth={2} dot={false} />
                  </AreaChart>
                </ResponsiveContainer>
              ) : <NoData />}
            </ChartCard>
          </div>
        </>
      )}

      {/* Instance ID */}
      {data?.instanceId && (
        <p className="text-xs text-gray-400 dark:text-gray-600 text-right">
          Instancia: <code className="font-mono">{data.instanceId}</code>
        </p>
      )}
    </div>
  );
};

// ─── helpers components ───────────────────────────────────────────────────────

const colorMap: Record<string, string> = {
  blue:   'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300',
  purple: 'bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300',
  green:  'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300',
  orange: 'bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300',
  teal:   'bg-teal-50 dark:bg-teal-900/20 text-teal-700 dark:text-teal-300',
};

const SummaryCard: React.FC<{ label: string; value: string; color: string }> = ({ label, value, color }) => (
  <div className={`rounded-lg p-3 border ${colorMap[color] ?? colorMap.blue} border-transparent`}>
    <p className="text-xs font-medium opacity-75">{label}</p>
    <p className="text-xl font-bold mt-0.5">{value}</p>
  </div>
);

const RangeSelector: React.FC<{
  rangeIdx: number;
  setRangeIdx: (i: number) => void;
  onRefresh: () => void;
}> = ({ rangeIdx, setRangeIdx, onRefresh }) => (
  <div className="flex items-center gap-2">
    {RANGES.map((r, i) => (
      <button
        key={r.label}
        onClick={() => setRangeIdx(i)}
        className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
          i === rangeIdx
            ? 'bg-black text-white dark:bg-white dark:text-black'
            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
        }`}
      >
        {r.label}
      </button>
    ))}
    <button
      onClick={onRefresh}
      title="Actualizar"
      className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
    >
      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
      </svg>
    </button>
  </div>
);

export default MonitoringTab;
